"use client"

import { useEffect, useRef } from 'react'
import { useTheme } from 'next-themes'

interface HTMLFrameProps {
  html: string
  className?: string
  height?: string
}

export default function HTMLFrame({ html, className = "", height = "500px" }: HTMLFrameProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const { resolvedTheme } = useTheme()
  const isDark = resolvedTheme === 'dark'

  // We use a high-performance rendering approach for complex data frames
  return (
    <div className={`w-full overflow-hidden rounded-sm border border-border bg-card p-1 my-8 shadow-sm transition-colors duration-500 ${className}`}>
      <div className="flex items-center justify-between px-3 py-2 bg-muted/30 border-b border-border">
        <div className="flex items-center gap-1.5">
          <div className="w-2 h-2 rounded-full bg-red-500/50" />
          <div className="w-2 h-2 rounded-full bg-amber-500/50" />
          <div className="w-2 h-2 rounded-full bg-emerald-500/50" />
        </div>
        <span className="text-[9px] font-bold uppercase tracking-widest text-muted-foreground/60">
          Interactive Data Canvas
        </span>
      </div>
      <iframe
        srcDoc={`
          <!DOCTYPE html>
          <html>
            <head>
               <meta charset="utf-8">
               <meta name="viewport" content="width=device-width, initial-scale=1">
               <style>
                  body { 
                    margin: 0; 
                    padding: 1.5rem; 
                    font-family: -apple-system, system-ui, sans-serif; 
                    background-color: ${isDark ? '#1a1a1a' : '#ffffff'};
                    color: ${isDark ? '#f1f1f1' : '#1a1a1a'};
                    line-height: 1.6;
                    transition: background-color 0.5s ease, color 0.5s ease;
                  }
                  canvas { max-width: 100%; height: auto !important; }
                  /* Ensure links and other elements inherit theme colors if they aren't hardcoded */
                  a { color: ${isDark ? '#ff0000' : '#d42121'}; }
                  * { border-color: ${isDark ? '#333333' : '#e5e7eb'}; }
               </style>
               <!-- Script support for chart.js if needed externally -->
               <script src="https://cdn.jsdelivr.net/npm/chart.js"></script>
            </head>
            <body class="${isDark ? 'dark' : 'light'}">
               ${html}
            </body>
          </html>
        `}
        className="w-full border-none"
        style={{ height }}
        title="Interactive Graphics Frame"
        sandbox="allow-scripts allow-modals allow-popups allow-forms"
      />
    </div>
  )
}
